import { log } from '@/log'
import { createFku as createFkuDefault, CreateFkuContext, Fku } from './item'

import { ApiProductclass } from '@/models/api/productclass'
import { FetchSkuPrice } from '@/models/item/sku/repository/fetch-sku-price'
import { DefaultFetchSkuPrice } from '@/models/item/sku/repository/impl/fetch-sku-price'
import BundleDiscountService, { BundleDiscount } from '@/services/api/bundle-discount'
import { FkuQuery } from '@/services/api/item/fku/fku-query'
import ProductclassService from '@/services/api/productclass'
import AppId from '@/util/appid'

export class FkuListRepository {
  static create(brandEnglishName: string): FkuListRepository {
    const appId = AppId.getByBrandName(brandEnglishName)

    return new FkuListRepository(
      new ProductclassService(appId),
      new BundleDiscountService(appId),
      DefaultFetchSkuPrice.create(brandEnglishName),
      createFkuDefault
    )
  }

  constructor(
    private readonly productclassService: ProductclassService,
    private readonly bundleDiscountService: BundleDiscountService,
    private readonly fetchSkuPrice: FetchSkuPrice,
    private readonly createFku: (raw: ApiProductclass, context: CreateFkuContext) => ReturnType<typeof createFkuDefault>
  ) {}

  async get(rootId: string): Promise<Fku[]> {
    const [
      productclassResult,
      bundleDiscountResult,
    ] = await Promise.all([
      this.productclassService.listFkuBelongingToRoot(rootId, FkuQuery.initialize({ with: 'part,stock~detail' })),
      // 同時に開催されるバンドル割引はそう多くないので、item idを指定することでキャッシュヒット率が下がることを避け全件取得している
      // 前提が変わればFKU IDを指定して取りに行ったほうが良い
      this.bundleDiscountService.listByItemIds([]),
    ])

    if (productclassResult.isLeft()) {
      throw productclassResult.value
    }
    const fkuProductclassList = productclassResult.value.map(raw => new ApiProductclass(raw))
    if (fkuProductclassList.length === 0) {
      throw new Error(`Could not fetch fku list for root id ${rootId}`)
    }

    const bundleDiscountList = bundleDiscountResult.mapLeft(
      (error): BundleDiscount[] => {
        // バンドル割引は表示用途にすぎないので、取得できなくてもエラーにはしない
        log.warn('fetch bundle discount list error:', error)

        return []
      }
    ).value

    log.debug({ fkuProductclassList, bundleDiscountList })

    const skuIdList = fkuProductclassList.reduce(
      (prev: string[], current: ApiProductclass): string[] => [
        ...prev,
        ...current.parts.map(part => part.id),
      ],
      []
    )

    const fetchSkuPriceResult = await this.fetchSkuPrice.fetch(skuIdList)
    if (fetchSkuPriceResult.isLeft()) {
      throw fetchSkuPriceResult.value
    }

    const createFkuContext = {
      skuPriceList: fetchSkuPriceResult.value,
      bundleDiscountList,
    }

    const fkuList = fkuProductclassList
      .sort((a, b) => a.displayLevel - b.displayLevel)
      .map((productclass): Fku | null =>
        this.createFku(productclass, createFkuContext).mapLeft((error): null => {
          log.error('failed to create fku:', error)

          return null
        }).value
      )
      .filter((fku): fku is Fku => fku !== null)

    if (fkuList.length === 0) {
      throw new Error(`Could not create fku list for root id ${rootId}`)
    }

    return fkuList
  }
}
